import { LeaderboardEntry, PersonalizedLeaderboardEntry, DailyUsageData, ProcessedAIData, NonAIDataResult, DataMode } from '../types';
import { fetchDailyUsageData, formatTimestampToDate } from './dailyUsageApiService';

import { fetchComprehensiveData, fetchManagerTeamData } from './multiTeamDataService';
import { NonAIDataService } from './nonAIDataService';
import { calculateCursorTime, calculateTimeStats, UserTimeData, TimeStats } from './timeCalculationService';

/**
 * Fetch weekly data from backend API (with backend S3 caching)
 * @param weekDate - Week date in YYYY-MM-DD format (any day of the week)
 * @returns Promise<LeaderboardEntry[]> - Processed leaderboard entries
 */
export const fetchWeeklyData = async (weekDate: string): Promise<LeaderboardEntry[]> => {
  try {
    console.log('🔄 [DATA] Fetching weekly data for:', weekDate);
    
    // Fetch data from the API (backend now handles S3 caching)
    const apiData = await fetchDailyUsageData(weekDate);
    
    // Map and aggregate the API data
    const mappedData = mapApiDataToLeaderboardEntries(apiData.data);
    const aggregatedData = aggregateDataByUser(mappedData);
    
    // Validate aggregation results
    validateAggregatedData(aggregatedData);
    
    console.log('✅ [DATA] Successfully processed weekly data:', {
      totalUsers: aggregatedData.length,
      source: 'backend-cached'
    });
    
    return aggregatedData;
  } catch (error) {
    console.error('🚨 [DATA] Error in fetchWeeklyData:', error);
    throw new Error(`Failed to fetch weekly data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Map API daily usage data to LeaderboardEntry format
 * @param dailyData - Array of daily usage data from API
 * @returns LeaderboardEntry[] - Mapped leaderboard entries
 */
export const mapApiDataToLeaderboardEntries = (dailyData: DailyUsageData[]): LeaderboardEntry[] => {
  console.log('🔄 [DATA] Mapping daily API data to leaderboard entries');
  
  const mapped = dailyData.map(data => ({
    userId: data.email.toLowerCase().trim(), // Use normalized email as userId
    email: data.email.toLowerCase().trim(), // Normalize email for consistent aggregation
    chatTotalApplies: data.totalApplies,
    chatTotalAccepts: data.totalAccepts,
    chatAcceptedLinesAdded: data.acceptedLinesAdded,
    totalLinesAdded: data.totalLinesAdded,
    chatTabsShown: data.totalTabsShown,
    tabsAccepted: data.totalTabsAccepted,
    editRequests: data.composerRequests,
    askRequests: data.chatRequests,
    agentRequests: data.agentRequests,
    cmdkRequests: data.cmdkUsages,
  }));
  
  console.log('✅ [DATA] Successfully mapped', mapped.length, 'daily entries to leaderboard format');
  return mapped;
};

/**
 * Aggregate data by user - sum metrics for each day into weekly totals
 * @param entries - Array of leaderboard entries (daily data - one record per user per day)
 * @returns LeaderboardEntry[] - Aggregated weekly data by user
 */
export const aggregateDataByUser = (entries: LeaderboardEntry[]): LeaderboardEntry[] => {
  console.log('🔄 [DATA] Aggregating daily entries into weekly totals, count:', entries.length);
  
  if (entries.length === 0) {
    console.warn('⚠️ [DATA] No entries to aggregate');
    return [];
  }
  
  // Check for potential data quality issues
  const allInputZero = entries.every(entry => 
    entry.chatTotalApplies === 0 && entry.chatAcceptedLinesAdded === 0 && entry.chatTabsShown === 0 && 
    entry.totalLinesAdded === 0 && entry.cmdkRequests === 0
  );
  
  if (allInputZero) {
    console.error('🚨 [DATA] All input data is zero - potential API issue');
  }
  
  // Group by email (normalized) to sum daily records into weekly totals
  const userMap = new Map<string, LeaderboardEntry>();
  
  entries.forEach((dailyEntry, index) => {
    const userEmail = dailyEntry.email.toLowerCase().trim();
    
    if (userMap.has(userEmail)) {
      // User already exists - sum up daily metrics
      const weeklyTotal = userMap.get(userEmail)!;
      
      weeklyTotal.chatTotalApplies += dailyEntry.chatTotalApplies;
      weeklyTotal.chatTotalAccepts += dailyEntry.chatTotalAccepts;
      weeklyTotal.chatAcceptedLinesAdded += dailyEntry.chatAcceptedLinesAdded;
      weeklyTotal.totalLinesAdded += dailyEntry.totalLinesAdded;
      weeklyTotal.chatTabsShown += dailyEntry.chatTabsShown;
      weeklyTotal.tabsAccepted += dailyEntry.tabsAccepted;
      weeklyTotal.editRequests += dailyEntry.editRequests;
      weeklyTotal.askRequests += dailyEntry.askRequests;
      weeklyTotal.agentRequests += dailyEntry.agentRequests;
      weeklyTotal.cmdkRequests += dailyEntry.cmdkRequests;
    } else {
      // First daily record for this user - create weekly entry
      const weeklyEntry: LeaderboardEntry = {
        userId: dailyEntry.email.toLowerCase().trim(), // Use email as userId for consistency
        email: userEmail,
        chatTotalApplies: dailyEntry.chatTotalApplies,
        chatTotalAccepts: dailyEntry.chatTotalAccepts,
        chatAcceptedLinesAdded: dailyEntry.chatAcceptedLinesAdded,
        totalLinesAdded: dailyEntry.totalLinesAdded,
        chatTabsShown: dailyEntry.chatTabsShown,
        tabsAccepted: dailyEntry.tabsAccepted,
        editRequests: dailyEntry.editRequests,
        askRequests: dailyEntry.askRequests,
        agentRequests: dailyEntry.agentRequests,
        cmdkRequests: dailyEntry.cmdkRequests
      };
      
      userMap.set(userEmail, weeklyEntry);
    }
  });
  
  const weeklyTotals = Array.from(userMap.values());
  
  console.log('✅ [DATA] Aggregation complete:', {
    dailyEntries: entries.length,
    uniqueUsers: weeklyTotals.length,
    avgDaysPerUser: (entries.length / weeklyTotals.length).toFixed(1)
  });
  
  // Check for potential aggregation issues
  const allOutputZero = weeklyTotals.every(entry => 
    entry.chatTotalApplies === 0 && entry.chatAcceptedLinesAdded === 0 && entry.chatTabsShown === 0 && 
    entry.totalLinesAdded === 0 && entry.cmdkRequests === 0
  );
  
  if (allOutputZero) {
    console.error('🚨 [DATA] Aggregation output is all zeros - potential logic issue');
  }

  return weeklyTotals;
};

/**
 * Sort users by a specific metric
 * @param users - Array of leaderboard entries
 * @param metric - Metric to sort by
 * @returns LeaderboardEntry[] - Sorted leaderboard entries
 */
export const sortUsersByMetric = (
  users: LeaderboardEntry[],
  metric: keyof Omit<LeaderboardEntry, 'userId' | 'email'>
): LeaderboardEntry[] => {
  console.log('🔄 [DATA] Sorting users by metric:', metric);
  
  const sorted = [...users].sort((a, b) => (b[metric] as number) - (a[metric] as number));
  
  console.log('✅ [DATA] Sorted', sorted.length, 'users by', metric);
  return sorted;
};

/**
 * Get available weeks (recent 8 weeks)
 * @returns Promise<string[]> - Array of available week dates
 */
export const getAvailableWeeks = async (): Promise<string[]> => {
  try {
    console.log('🔄 [DATA] Getting available weeks');
    
    // Return a range of recent weeks (last 8 weeks)
    const weeks: string[] = [];
    const today = new Date();
    
    for (let i = 0; i < 8; i++) {
      const weekDate = new Date(today);
      weekDate.setDate(today.getDate() - (i * 7));
      weeks.push(weekDate.toISOString().split('T')[0]);
    }
    
    console.log('✅ [DATA] Available weeks:', weeks);
    return weeks;
  } catch (error) {
    console.error('🚨 [DATA] Error getting available weeks:', error);
    throw new Error('Failed to get available weeks');
  }
}; 

/**
 * Validate weekly aggregated data
 * @param weeklyData - Weekly aggregated leaderboard entries
 * @returns boolean - True if validation passes
 */
export const validateAggregatedData = (weeklyData: LeaderboardEntry[]): boolean => {
  console.log('🔍 [DATA] Validating weekly aggregated data...');
  
  if (weeklyData.length === 0) {
    console.warn('⚠️ [DATA] No weekly data to validate');
    return false;
  }
  
  // Basic validation - check for reasonable data
  const validEntries = weeklyData.filter(entry => 
    entry.email && 
    entry.email.includes('@') &&
    typeof entry.chatTotalApplies === 'number' &&
    entry.chatTotalApplies >= 0
  );
  
  const validationResults = {
    totalEntries: weeklyData.length,
    validEntries: validEntries.length,
    invalidEntries: weeklyData.length - validEntries.length,
    totalApplies: weeklyData.reduce((sum, entry) => sum + entry.chatTotalApplies, 0),
    totalAccepts: weeklyData.reduce((sum, entry) => sum + entry.chatTotalAccepts, 0),
    totalLinesAdded: weeklyData.reduce((sum, entry) => sum + entry.chatAcceptedLinesAdded, 0)
  };
  
  console.log('📊 [DATA] Weekly data validation results:', validationResults);
  
  if (validationResults.invalidEntries > 0) {
    console.warn('⚠️ [DATA] Found invalid entries during validation');
    return false;
  }
  
  console.log('✅ [DATA] Weekly data validation passed');
  return true;
};

// debugDailyData function removed - was unused





// ============================================================================
// MONTHLY REPORTS FUNCTIONALITY
// ============================================================================

/**
 * Calculate 4 consecutive week dates starting from a given Monday
 * @param startDate - Starting Monday date in YYYY-MM-DD format
 * @returns string[] - Array of 4 consecutive Monday dates
 */
export const getConsecutiveWeekDates = (startDate: string, weekCount: number = 4): string[] => {
  const dates: string[] = [];
  const start = new Date(startDate + 'T00:00:00.000Z');
  
  // Ensure we start on a Monday
  const dayOfWeek = start.getUTCDay();
  if (dayOfWeek !== 1) {
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday = 0, so we need 6 days back
    start.setUTCDate(start.getUTCDate() - daysToSubtract);
  }
  
  for (let i = 0; i < weekCount; i++) {
    const weekDate = new Date(start);
    weekDate.setUTCDate(start.getUTCDate() + (i * 7));
    dates.push(formatTimestampToDate(weekDate.getTime()));
  }
  
  console.log(`📅 [MONTHLY] Generated ${weekCount} consecutive week dates from ${startDate}:`, dates);
  return dates;
};

/**
 * Validate that a given date string represents a Monday
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns boolean - True if the date is a Monday
 */
export const validateMondayStart = (dateString: string): boolean => {
  const date = new Date(dateString + 'T00:00:00.000Z');
  return date.getUTCDay() === 1; // Monday = 1
};

/**
 * Aggregate multiple weeks of LeaderboardEntry arrays into monthly totals
 * @param weeklyResults - Array of weekly LeaderboardEntry arrays
 * @returns LeaderboardEntry[] - Monthly aggregated data
 */
export const aggregateWeeklyDataToMonthly = (weeklyResults: LeaderboardEntry[][]): LeaderboardEntry[] => {
  console.log('🔄 [MONTHLY] Aggregating weekly data into monthly totals');
  console.log('📊 [MONTHLY] Weekly data summary:', {
    weekCount: weeklyResults.length,
    weekSizes: weeklyResults.map(week => week.length),
    totalRecords: weeklyResults.reduce((sum, week) => sum + week.length, 0)
  });

  if (weeklyResults.length === 0) {
    console.warn('⚠️ [MONTHLY] No weekly data provided for aggregation');
    return [];
  }

  // Flatten all weekly data into a single array for aggregation
  const allEntries: LeaderboardEntry[] = [];
  weeklyResults.forEach((weekData, weekIndex) => {
    console.log(`📅 [MONTHLY] Processing week ${weekIndex + 1} with ${weekData.length} users`);
    allEntries.push(...weekData);
  });

  // Use existing aggregation logic to sum all entries by user
  const monthlyTotals = aggregateDataByUser(allEntries);

  console.log('✅ [MONTHLY] Monthly aggregation complete:', {
    weekCount: weeklyResults.length,
    totalInputRecords: allEntries.length,
    uniqueUsers: monthlyTotals.length,
    avgWeeksPerUser: (allEntries.length / monthlyTotals.length).toFixed(1)
  });

  return monthlyTotals;
};

/**
 * Frontend cache for monthly data to avoid re-aggregating same periods
 */
class MonthlyDataCache {
  private cache = new Map<string, { data: LeaderboardEntry[]; timestamp: number }>();
  private readonly maxAge = 30 * 60 * 1000; // 30 minutes

  getCacheKey(startDate: string): string {
    return `monthly_${startDate}`;
  }

  get(startDate: string): LeaderboardEntry[] | null {
    const key = this.getCacheKey(startDate);
    const cached = this.cache.get(key);
    
    if (cached && (Date.now() - cached.timestamp < this.maxAge)) {
      console.log('📦 [MONTHLY-CACHE] Cache hit for:', startDate);
      return cached.data;
    }
    
    if (cached) {
      console.log('📦 [MONTHLY-CACHE] Cache expired for:', startDate);
      this.cache.delete(key);
    }
    
    return null;
  }

  set(startDate: string, data: LeaderboardEntry[]): void {
    const key = this.getCacheKey(startDate);
    this.cache.set(key, { data, timestamp: Date.now() });
    console.log('📦 [MONTHLY-CACHE] Cached monthly data for:', startDate);
  }

  clear(): void {
    this.cache.clear();
    console.log('📦 [MONTHLY-CACHE] Cache cleared');
  }
}

// Global monthly cache instance
const monthlyCache = new MonthlyDataCache();

/**
 * Fetch and aggregate 4 weeks of data into monthly totals
 * @param monthStartDate - Starting Monday date for the 4-week period (YYYY-MM-DD format)
 * @returns Promise<LeaderboardEntry[]> - Monthly aggregated leaderboard data
 */
export const fetchMonthlyData = async (monthStartDate: string): Promise<LeaderboardEntry[]> => {
  try {
    console.log('🔄 [MONTHLY] Fetching monthly data starting from:', monthStartDate);

    // Check cache first
    const cachedData = monthlyCache.get(monthStartDate);
    if (cachedData) {
      return cachedData;
    }

    // Validate starting date is a Monday
    if (!validateMondayStart(monthStartDate)) {
      console.warn('⚠️ [MONTHLY] Start date is not a Monday, adjusting...', monthStartDate);
    }

    // Calculate 4 consecutive week dates
    const weekDates = getConsecutiveWeekDates(monthStartDate, 4);
    
    // Fetch all 4 weeks in parallel
    console.log('🔄 [MONTHLY] Fetching 4 weeks in parallel:', weekDates);
    const weeklyPromises = weekDates.map(async (weekDate, index) => {
      try {
        console.log(`📅 [MONTHLY] Fetching week ${index + 1}: ${weekDate}`);
        const weekData = await fetchWeeklyData(weekDate);
        console.log(`✅ [MONTHLY] Week ${index + 1} complete: ${weekData.length} users`);
        return weekData;
      } catch (error) {
        console.error(`🚨 [MONTHLY] Week ${index + 1} failed (${weekDate}):`, error);
        throw error; // Re-throw to be handled by Promise.allSettled
      }
    });

    // Use allSettled to handle partial failures gracefully
    const results = await Promise.allSettled(weeklyPromises);
    
    // Process results and identify failures
    const successfulWeeks: LeaderboardEntry[][] = [];
    const failedWeeks: string[] = [];
    
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        successfulWeeks.push(result.value);
      } else {
        failedWeeks.push(weekDates[index]);
        console.error(`🚨 [MONTHLY] Failed to fetch week ${index + 1} (${weekDates[index]}):`, result.reason);
      }
    });

    // Check if we have enough data to proceed
    if (successfulWeeks.length === 0) {
      throw new Error('No weekly data available for monthly aggregation');
    }

    if (failedWeeks.length > 0) {
      const failureRate = (failedWeeks.length / 4) * 100;
      console.warn(`⚠️ [MONTHLY] Partial data: ${successfulWeeks.length}/4 weeks successful (${failureRate.toFixed(0)}% failure rate)`);
      
      if (successfulWeeks.length < 2) {
        throw new Error(`Insufficient weekly data for monthly report (only ${successfulWeeks.length}/4 weeks available)`);
      }
    }

    // Aggregate all successful weeks into monthly totals
    const monthlyData = aggregateWeeklyDataToMonthly(successfulWeeks);

    // Cache the result
    monthlyCache.set(monthStartDate, monthlyData);

    console.log('✅ [MONTHLY] Monthly data fetch complete:', {
      startDate: monthStartDate,
      weekDates,
      successfulWeeks: successfulWeeks.length,
      failedWeeks: failedWeeks.length,
      monthlyUsers: monthlyData.length,
      partialData: failedWeeks.length > 0
    });

    return monthlyData;
  } catch (error) {
    console.error('🚨 [MONTHLY] Error in fetchMonthlyData:', error);
    throw new Error(`Failed to fetch monthly data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Clear monthly data cache (useful for testing or forced refresh)
 */
export const clearMonthlyCache = (): void => {
  monthlyCache.clear();
};

/**
 * Fetch comprehensive 4-weekly data using both Cursor and Crossover APIs
 * This function implements the comprehensive API integration approach from the implementation plan
 * @param startDate - Starting Monday date for the 4-week period (YYYY-MM-DD format)
 * @param dataMode - Data mode ('basic', 'central-ps', 'my-team')
 * @param authState - Authentication state for API calls
 * @returns Promise<LeaderboardEntry[] | PersonalizedLeaderboardEntry[]> - 4-weekly aggregated comprehensive data
 */
export const fetch4WeeklyData = async (
  startDate: string,
  dataMode: 'basic' | 'central-ps' | 'my-team',
  authState: { token: string; managerId: number | null }
): Promise<LeaderboardEntry[] | PersonalizedLeaderboardEntry[]> => {
  console.log('🔄 [4-WEEKLY] Starting comprehensive 4-weekly data fetch:', {
    startDate,
    dataMode,
    managerId: authState.managerId
  });

  try {
    // Import required services (avoiding top-level imports to prevent circular dependencies)
    const { fetchComprehensiveData, fetchManagerTeamData } = await import('./multiTeamDataService');
    const { getFilteredLeaderboardData } = await import('./teamFilteringService');
    const { formatTimestampToDate, getMondayMidnightUTC } = await import('./dailyUsageApiService');
    const { getSessionCache } = await import('./sessionCacheService');

    // Check session cache first
    const sessionCache = getSessionCache();
    const cachedData = sessionCache.getCachedData(dataMode, startDate);
    if (cachedData) {
      console.log('🚀 [4-WEEKLY] Session cache hit for:', dataMode, startDate);
      return cachedData;
    }

    // Calculate 4 consecutive week dates
    const weekDates = getConsecutiveWeekDates(startDate, 4);
    console.log('📅 [4-WEEKLY] Processing 4 weeks:', weekDates);

    // Parallel fetch of cursor data for all 4 weeks
    console.log('📊 [4-WEEKLY] Fetching cursor data for 4 weeks in parallel...');
    const cursorDataPromises = weekDates.map(async (weekDate, index) => {
      try {
        console.log(`📊 [4-WEEKLY] Fetching cursor week ${index + 1}: ${weekDate}`);
        const data = await fetchWeeklyData(weekDate);
        console.log(`✅ [4-WEEKLY] Cursor week ${index + 1} complete: ${data.length} users`);
        return { weekDate, data, success: true };
      } catch (error) {
        console.error(`🚨 [4-WEEKLY] Cursor week ${index + 1} failed (${weekDate}):`, error);
        return { weekDate, data: [], success: false, error };
      }
    });

    // Parallel fetch of crossover data for all 4 weeks (based on data mode)
    console.log('🏢 [4-WEEKLY] Fetching crossover data for 4 weeks in parallel...');
    const crossoverDataPromises = weekDates.map(async (weekDate, index) => {
      try {
        console.log(`🏢 [4-WEEKLY] Fetching crossover week ${index + 1}: ${weekDate}`);
        
        // Convert to Monday date format for API
        const mondayTimestamp = getMondayMidnightUTC(weekDate);
        const mondayDate = formatTimestampToDate(mondayTimestamp);
        
        let data;
        if (dataMode === 'central-ps') {
          // Use comprehensive data for Central PS
          data = await fetchComprehensiveData(mondayDate);
        } else if (dataMode === 'my-team' && authState.managerId) {
          // Use manager team data for My Team
          data = await fetchManagerTeamData(authState.managerId, mondayDate, authState.token);
        } else {
          // Basic mode doesn't need crossover data
          data = { teams: [], activities: [] };
        }
        
        console.log(`✅ [4-WEEKLY] Crossover week ${index + 1} complete: ${data.activities.length} activities`);
        return { weekDate, data, success: true };
      } catch (error) {
        console.error(`🚨 [4-WEEKLY] Crossover week ${index + 1} failed (${weekDate}):`, error);
        return { weekDate, data: { teams: [], activities: [] }, success: false, error };
      }
    });

    // Execute both sets of API calls in parallel
    console.log('⚡ [4-WEEKLY] Executing all 8 API calls (4 cursor + 4 crossover) in parallel...');
    const [cursorResults, crossoverResults] = await Promise.all([
      Promise.all(cursorDataPromises),
      Promise.all(crossoverDataPromises)
    ]);

    // Process results and check for failures
    const successfulCursorWeeks = cursorResults.filter(r => r.success).map(r => r.data);
    const successfulCrossoverWeeks = crossoverResults.filter(r => r.success).map(r => r.data);
    const failedCursorWeeks = cursorResults.filter(r => !r.success).length;
    const failedCrossoverWeeks = crossoverResults.filter(r => !r.success).length;

    console.log('📊 [4-WEEKLY] API call results:', {
      successfulCursorWeeks: successfulCursorWeeks.length,
      successfulCrossoverWeeks: successfulCrossoverWeeks.length,
      failedCursorWeeks,
      failedCrossoverWeeks
    });

    // Check if we have sufficient data (at least 3/4 weeks from both APIs)
    if (successfulCursorWeeks.length < 3) {
      throw new Error(`Insufficient cursor data for 4-weekly report (only ${successfulCursorWeeks.length}/4 weeks available)`);
    }

    if (dataMode !== 'basic' && successfulCrossoverWeeks.length < 3) {
      throw new Error(`Insufficient team data for 4-weekly report (only ${successfulCrossoverWeeks.length}/4 weeks available)`);
    }

    // Aggregate the 4-weekly data
    let aggregatedData: LeaderboardEntry[];
    
    if (dataMode === 'basic') {
      // Basic mode: just aggregate cursor data
      aggregatedData = aggregate4WeeklyBasicData(successfulCursorWeeks);
    } else {
      // Enhanced modes: integrate cursor and crossover data
      aggregatedData = await aggregate4WeeklyComprehensiveData(
        successfulCursorWeeks,
        successfulCrossoverWeeks,
        dataMode,
        authState.managerId!
      );
    }

    // Cache the result for the session
    sessionCache.cacheData(dataMode, startDate, aggregatedData);

    console.log('✅ [4-WEEKLY] 4-weekly data aggregation complete:', {
      startDate,
      dataMode,
      totalUsers: aggregatedData.length,
      cursorWeeksUsed: successfulCursorWeeks.length,
      crossoverWeeksUsed: successfulCrossoverWeeks.length,
      partialData: failedCursorWeeks > 0 || failedCrossoverWeeks > 0
    });

    return aggregatedData;

  } catch (error) {
    console.error('🚨 [4-WEEKLY] Error in fetch4WeeklyData:', error);
    throw new Error(`Failed to fetch 4-weekly data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Aggregate 4 weeks of basic cursor data (for basic mode)
 * Sums all cursor metrics across 4 weeks per user
 * @param weeklyDataSets - Array of weekly cursor data arrays
 * @returns LeaderboardEntry[] - Aggregated 4-weekly data
 */
export const aggregate4WeeklyBasicData = (weeklyDataSets: LeaderboardEntry[][]): LeaderboardEntry[] => {
  console.log('🔄 [4-WEEKLY-AGG] Aggregating basic cursor data for 4 weeks');
  
  if (weeklyDataSets.length === 0) {
    console.warn('⚠️ [4-WEEKLY-AGG] No weekly data to aggregate');
    return [];
  }

  // Combine all weeks into one array and use existing aggregation logic
  const allWeeklyEntries = weeklyDataSets.flat();
  
  console.log('📊 [4-WEEKLY-AGG] Total entries across all weeks:', allWeeklyEntries.length);
  
  // Use existing aggregation function to sum across all weeks
  const aggregatedData = aggregateDataByUser(allWeeklyEntries);
  
  console.log('✅ [4-WEEKLY-AGG] Basic 4-weekly aggregation complete:', {
    weeksProcessed: weeklyDataSets.length,
    totalEntries: allWeeklyEntries.length,
    uniqueUsers: aggregatedData.length
  });
  
  return aggregatedData;
};

/**
 * Aggregate 4 weeks of comprehensive data (cursor + crossover for enhanced modes)
 * Implements team member summation across 4 weeks with cross-API data integration
 * @param cursorWeeks - Array of weekly cursor data arrays
 * @param crossoverWeeks - Array of weekly crossover data arrays  
 * @param dataMode - Data mode ('central-ps' or 'my-team')
 * @param managerId - Manager ID for filtering
 * @returns Promise<PersonalizedLeaderboardEntry[]> - Comprehensive 4-weekly aggregated data
 */
export const aggregate4WeeklyComprehensiveData = async (
  cursorWeeks: LeaderboardEntry[][],
  crossoverWeeks: any[],
  dataMode: 'central-ps' | 'my-team',
  managerId: number
): Promise<PersonalizedLeaderboardEntry[]> => {
  console.log('🔄 [4-WEEKLY-COMP] Aggregating comprehensive data for 4 weeks:', {
    dataMode,
    cursorWeeks: cursorWeeks.length,
    crossoverWeeks: crossoverWeeks.length,
    managerId
  });

  try {
    // Import filtering service
    const { getFilteredLeaderboardData } = await import('./teamFilteringService');

    // Step 1: Aggregate cursor data across all 4 weeks
    const aggregatedCursorData = aggregate4WeeklyBasicData(cursorWeeks);
    console.log('📊 [4-WEEKLY-COMP] Cursor data aggregated:', aggregatedCursorData.length, 'users');

    // Step 2: Aggregate crossover data across 4 weeks (team members need to be summed like cursor data)
    const allTeams: any[] = [];
    const allActivities: any[] = [];
    
    crossoverWeeks.forEach((weekData, index) => {
      if (weekData.teams) {
        allTeams.push(...weekData.teams);
      }
      if (weekData.activities) {
        allActivities.push(...weekData.activities);
      }
      console.log(`📊 [4-WEEKLY-COMP] Week ${index + 1}: ${weekData.activities?.length || 0} activities, ${weekData.teams?.length || 0} teams`);
    });

    // Remove duplicate teams (same team across multiple weeks)
    const uniqueTeams = Array.from(
      new Map(allTeams.map(team => [team.id, team])).values()
    );

    // CRITICAL FIX: Aggregate team activities by team member across 4 weeks
    const aggregatedActivities = aggregateTeamActivitiesByMember(allActivities);
    console.log('📊 [4-WEEKLY-COMP] Team activities aggregated:', {
      originalActivities: allActivities.length,
      aggregatedMembers: aggregatedActivities.length
    });

    const combinedCrossoverData = {
      teams: uniqueTeams,
      activities: aggregatedActivities, // Use aggregated activities instead of raw activities
      summary: {
        totalTeams: uniqueTeams.length,
        totalManagers: new Set(aggregatedActivities.map(a => a.managerId)).size,
        totalTeamMembers: aggregatedActivities.length,
        requestedManagerId: managerId.toString(),
        dateRequested: new Date().toISOString(),
        timestamp: new Date().toISOString()
      }
    };

    console.log('📊 [4-WEEKLY-COMP] Combined crossover data:', {
      totalActivities: allActivities.length,
      uniqueTeams: uniqueTeams.length
    });

    // Step 3: Apply filtering and data integration using existing logic
    const filteredScope = dataMode === 'central-ps' ? 'global' : 'team';
    const comprehensiveData = getFilteredLeaderboardData(
      combinedCrossoverData,
      aggregatedCursorData,
      managerId,
      dataMode,
      filteredScope
    );

    console.log('✅ [4-WEEKLY-COMP] Comprehensive 4-weekly aggregation complete:', {
      dataMode,
      filteredScope,
      totalUsers: comprehensiveData.length,
      withCursorData: comprehensiveData.filter(u => u.cursorTimeMinutes && u.cursorTimeMinutes > 0).length,
      teamMembersOnly: comprehensiveData.filter(u => !u.cursorTimeMinutes || u.cursorTimeMinutes === 0).length
    });

    return comprehensiveData;

  } catch (error) {
    console.error('🚨 [4-WEEKLY-COMP] Error in comprehensive aggregation:', error);
    throw new Error(`Failed to aggregate comprehensive 4-weekly data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Aggregate team activities by team member across multiple weeks
 * This is critical for 4-weekly reports - each team member appears once per week and needs to be summed
 * Uses standardized timeCalculationService for consistent aggregation
 * @param activities - Array of team activities from multiple weeks
 * @returns Aggregated activities with properly summed time metrics per team member
 */
export const aggregateTeamActivitiesByMember = (activities: any[]): any[] => {
  console.log('🔄 [TEAM-AGG] Aggregating team activities by member across weeks:', activities.length);
  
  if (activities.length === 0) {
    console.warn('⚠️ [TEAM-AGG] No activities to aggregate');
    return [];
  }

  // Group activities by team member (using candidate ID as unique identifier)
  const memberMap = new Map<string, any[]>();

  // First pass: Group all activities by team member
  activities.forEach((activity, index) => {
    const candidateId = activity.assignment?.candidate?.id?.toString();
    
    if (!candidateId) {
      console.warn(`⚠️ [TEAM-AGG] Skipping activity ${index} - no candidate ID`);
      return;
    }

    if (!memberMap.has(candidateId)) {
      memberMap.set(candidateId, []);
    }
    memberMap.get(candidateId)!.push(activity);
  });

  // Second pass: Aggregate each member's activities properly
  const aggregatedActivities: any[] = [];
  
  for (const [candidateId, memberActivities] of memberMap.entries()) {
    try {
      const aggregatedActivity = aggregateActivitiesForSingleMember(memberActivities);
      aggregatedActivities.push(aggregatedActivity);
      
      // Log aggregation results using calculateTimeStats for verification
      const timeStats = calculateTimeStats({
        grouping: aggregatedActivity.grouping
      });
      
      console.log(`📊 [TEAM-AGG] Aggregated member ${candidateId}:`, {
        weekCount: memberActivities.length,
        cursorTime: timeStats.cursorTime,
        aiTime: timeStats.aiTime,
        nonAiTime: timeStats.nonAiTime,
        totalTime: timeStats.totalTime
      });
    } catch (error) {
      console.error(`🚨 [TEAM-AGG] Failed to aggregate member ${candidateId}:`, error);
      // Fall back to first activity if aggregation fails
      aggregatedActivities.push(memberActivities[0]);
    }
  }
  
  console.log('✅ [TEAM-AGG] Team activity aggregation complete:', {
    originalActivities: activities.length,
    uniqueMembers: aggregatedActivities.length,
    totalMembers: memberMap.size
  });

  return aggregatedActivities;
};

/**
 * Aggregate multiple activities for a single team member across weeks
 * Properly sums all time data while preserving the activity structure
 * @param memberActivities - Array of activities for the same team member across multiple weeks
 * @returns Single aggregated activity with summed time metrics
 */
const aggregateActivitiesForSingleMember = (memberActivities: any[]): any => {
  if (memberActivities.length === 0) {
    throw new Error('Cannot aggregate empty activity list');
  }
  
  if (memberActivities.length === 1) {
    return { ...memberActivities[0] };
  }

  // Use the first activity as the base structure
  const baseActivity = { ...memberActivities[0] };
  
  // Calculate time stats for each week using standardized service
  const weeklyTimeStats = memberActivities.map(activity => 
    calculateTimeStats({
      grouping: activity.grouping || { advancedGroups: [] }
    })
  );
  
  // Sum up all time components
  const aggregatedTimeStats = weeklyTimeStats.reduce(
    (total, weekStats) => ({
      cursorTime: total.cursorTime + weekStats.cursorTime,
      aiTime: total.aiTime + weekStats.aiTime,
      nonAiTime: total.nonAiTime + weekStats.nonAiTime,
      totalTime: total.totalTime + weekStats.totalTime
    }),
    { cursorTime: 0, aiTime: 0, nonAiTime: 0, totalTime: 0 }
  );
  
  // Create aggregated advanced groups structure
  const aggregatedGroups = createAggregatedAdvancedGroups(memberActivities, aggregatedTimeStats);
  
  // Update the base activity with aggregated data
  baseActivity.grouping = {
    ...baseActivity.grouping,
    totalTrackedTime: aggregatedTimeStats.totalTime,
    advancedGroups: aggregatedGroups,
    periodLong: memberActivities.reduce((sum, activity) => 
      sum + (activity.grouping?.periodLong || 0), 0
    )
  };
  
  return baseActivity;
};

/**
 * Create aggregated advanced groups structure from multiple activities
 * Preserves the structure while summing time values correctly
 */
const createAggregatedAdvancedGroups = (memberActivities: any[], timeStats: any): any[] => {
  // Create a map to aggregate applications by name within each section
  const sectionMap = new Map<string, Map<string, any>>();
  
  // Process each week's data
  memberActivities.forEach(activity => {
    const groups = activity.grouping?.advancedGroups || [];
    
    groups.forEach((group: any) => {
      const sectionName = group.sectionName;
      
      if (!sectionMap.has(sectionName)) {
        sectionMap.set(sectionName, new Map());
      }
      
      const sectionApps = sectionMap.get(sectionName)!;
      
      // Aggregate applications within this section
      if (group.groupItems) {
        group.groupItems.forEach((app: any) => {
          const appName = app.applicationName;
          
          if (sectionApps.has(appName)) {
            // Sum the time for existing application
            const existingApp = sectionApps.get(appName);
            existingApp.spentTime += app.spentTime || 0;
          } else {
            // Add new application
            sectionApps.set(appName, {
              ...app,
              spentTime: app.spentTime || 0
            });
          }
        });
      }
    });
  });
  
  // Convert back to the expected structure
  const aggregatedGroups: any[] = [];
  
  for (const [sectionName, apps] of sectionMap.entries()) {
    const groupItems = Array.from(apps.values());
    const sectionTime = groupItems.reduce((sum, app) => sum + app.spentTime, 0);
    
    aggregatedGroups.push({
      sectionName,
      groupItems,
      spentTime: sectionTime,
      color: groupItems[0]?.categoryColor || '#cccccc'
    });
  }
  
  return aggregatedGroups;
};

/**
 * Extract cursor time from team activity (helper function)
 * Uses the new standardized time calculation service
 */
const extractCursorTimeFromActivity = (activity: any): number => {
  try {
    // Convert to UserTimeData format for the new calculation service
    const userData: UserTimeData = {
      grouping: {
        periodLong: activity.grouping?.periodLong || 0,
        totalTrackedTime: activity.grouping?.totalTrackedTime || 0,
        alignmentScore: activity.grouping?.alignmentScore || 0,
        advancedGroups: activity.grouping?.advancedGroups || []
      }
    };
    
    // Use the new standardized calculation service
    return calculateCursorTime(userData.grouping.advancedGroups);
  } catch (error) {
    console.warn('⚠️ [TEAM-AGG] Error extracting cursor time:', error);
    return 0;
  }
};

/**
 * Update cursor time in team activity (helper function)
 * This distributes the new total time proportionally across all cursor items
 */
const updateCursorTimeInActivity = (activity: any, newCursorTime: number): any => {
  try {
    // Clone the activity to avoid mutations
    const updatedActivity = { ...activity };
    
    // Get current cursor time to calculate proportions
    const currentCursorTime = extractCursorTimeFromActivity(activity);
    
    // If no current cursor time, we can't distribute proportionally
    if (currentCursorTime === 0) {
      console.warn('⚠️ [TEAM-AGG] No existing cursor time to update proportionally');
      return activity;
    }
    
    const scaleFactor = newCursorTime / currentCursorTime;
    
    // Deep clone and update the grouping structure
    if (updatedActivity.grouping?.advancedGroups) {
      updatedActivity.grouping = {
        ...updatedActivity.grouping,
        advancedGroups: updatedActivity.grouping.advancedGroups.map((group: any) => {
          // Only update AI-related sections
          if (group.sectionName === 'PURE_AI' || group.sectionName === 'AI') {
            return {
              ...group,
              groupItems: group.groupItems?.map((item: any) => {
                // Only update cursor items proportionally
                if (item.applicationName?.toLowerCase().includes('cursor')) {
                  return {
                    ...item,
                    spentTime: Math.round((item.spentTime || 0) * scaleFactor)
                  };
                }
                return item;
              }) || []
            };
          }
          return group;
        })
      };
    }
    
    return updatedActivity;
  } catch (error) {
    console.warn('⚠️ [TEAM-AGG] Error updating cursor time, returning original activity:', error);
    return activity;
  }
};

/**
 * Fetch and aggregate AI data for 4-weekly reports
 * This function fetches 4 weeks of team activities and processes them for AI Overview
 * @param startDate - Starting Monday date for the 4-week period (YYYY-MM-DD format)
 * @param dataMode - Data mode ('central-ps', 'my-team')
 * @param authState - Authentication state for API calls
 * @returns Promise<ProcessedAIData> - 4-weekly aggregated AI data
 */
export const fetch4WeeklyAIData = async (
  startDate: string,
  dataMode: 'central-ps' | 'my-team',
  authState: { token: string; managerId: number | null }
): Promise<ProcessedAIData> => {
  console.log('🤖 [4-WEEKLY-AI] Starting 4-weekly AI data fetch:', {
    startDate,
    dataMode,
    managerId: authState.managerId
  });

  try {
    // Import required services
    const { fetchComprehensiveData, fetchManagerTeamData } = await import('./multiTeamDataService');
    const { filterActivitiesForCentralPS } = await import('./teamFilteringService');
    const { processComprehensiveAIData } = await import('./aiOverviewService');
    const { formatTimestampToDate, getMondayMidnightUTC } = await import('./dailyUsageApiService');

    // Simple in-memory cache for AI data (session-based)
    const aiCacheKey = `4weekly-ai-${dataMode}-${startDate}`;
    if (typeof window !== 'undefined' && (window as any).__4weeklyAICache) {
      const cached = (window as any).__4weeklyAICache[aiCacheKey];
      if (cached) {
        console.log('🚀 [4-WEEKLY-AI] Cache hit for AI data:', dataMode, startDate);
        return cached;
      }
    }

    // Calculate 4 consecutive week dates
    const weekDates = getConsecutiveWeekDates(startDate, 4);
    console.log('📅 [4-WEEKLY-AI] Processing AI data for 4 weeks:', weekDates);

    // Fetch crossover data for all 4 weeks (we only need team activities for AI data)
    console.log('🏢 [4-WEEKLY-AI] Fetching crossover data for 4 weeks in parallel...');
    const crossoverDataPromises = weekDates.map(async (weekDate, index) => {
      try {
        console.log(`🏢 [4-WEEKLY-AI] Fetching AI week ${index + 1}: ${weekDate}`);
        
        // Convert to Monday date format for API
        const mondayTimestamp = getMondayMidnightUTC(weekDate);
        const mondayDate = formatTimestampToDate(mondayTimestamp);
        
        let data;
        if (dataMode === 'central-ps') {
          // Use comprehensive data for Central PS
          data = await fetchComprehensiveData(mondayDate);
        } else if (dataMode === 'my-team' && authState.managerId) {
          // Use manager team data for My Team
          data = await fetchManagerTeamData(authState.managerId, mondayDate, authState.token);
        } else {
          throw new Error('Invalid data mode or missing managerId for AI data');
        }
        
        console.log(`✅ [4-WEEKLY-AI] AI week ${index + 1} complete: ${data.activities.length} activities`);
        return { weekDate, data, success: true };
      } catch (error) {
        console.error(`🚨 [4-WEEKLY-AI] AI week ${index + 1} failed (${weekDate}):`, error);
        return { weekDate, data: { teams: [], activities: [] }, success: false, error };
      }
    });

    // Execute all crossover API calls
    const crossoverResults = await Promise.all(crossoverDataPromises);

    // Process results and check for failures
    const successfulCrossoverWeeks = crossoverResults.filter(r => r.success).map(r => r.data);
    const failedCrossoverWeeks = crossoverResults.filter(r => !r.success).length;

    console.log('🤖 [4-WEEKLY-AI] API call results:', {
      successfulCrossoverWeeks: successfulCrossoverWeeks.length,
      failedCrossoverWeeks
    });

    // Check if we have sufficient data (at least 3/4 weeks)
    if (successfulCrossoverWeeks.length < 3) {
      throw new Error(`Insufficient team data for 4-weekly AI report (only ${successfulCrossoverWeeks.length}/4 weeks available)`);
    }

    // Aggregate team activities across 4 weeks
    const allActivities: any[] = [];
    const allTeams: any[] = [];
    
    successfulCrossoverWeeks.forEach((weekData, index) => {
      if (weekData.teams) {
        allTeams.push(...weekData.teams);
      }
      if (weekData.activities) {
        allActivities.push(...weekData.activities);
      }
      console.log(`🤖 [4-WEEKLY-AI] Week ${index + 1}: ${weekData.activities?.length || 0} activities for AI processing`);
    });

    // Aggregate activities by team member (same logic as leaderboard)
    const aggregatedActivities = aggregateTeamActivitiesByMember(allActivities);
    console.log('🤖 [4-WEEKLY-AI] Team activities aggregated for AI:', {
      originalActivities: allActivities.length,
      aggregatedMembers: aggregatedActivities.length
    });

    // Apply filtering if needed (for Central PS mode)
    let filteredActivities = aggregatedActivities;
    if (dataMode === 'central-ps' && authState.managerId) {
      // Remove duplicate teams (same team across multiple weeks)
      const uniqueTeams = Array.from(
        new Map(allTeams.map(team => [team.id, team])).values()
      );
      
      filteredActivities = filterActivitiesForCentralPS(
        aggregatedActivities,
        authState.managerId,
        uniqueTeams,
        'global'
      );
      console.log('🏢 [4-WEEKLY-AI] Applied Central PS filtering:', {
        originalActivities: aggregatedActivities.length,
        filteredActivities: filteredActivities.length
      });
    }

    // Process aggregated activities through AI processing logic
    const processedAIData = processComprehensiveAIData(filteredActivities);

    // Cache the result for the session using simple in-memory cache
    if (typeof window !== 'undefined') {
      if (!(window as any).__4weeklyAICache) {
        (window as any).__4weeklyAICache = {};
      }
      (window as any).__4weeklyAICache[aiCacheKey] = processedAIData;
      console.log('💾 [4-WEEKLY-AI] Cached AI data for session:', aiCacheKey);
    }

    console.log('✅ [4-WEEKLY-AI] 4-weekly AI data processing complete:', {
      startDate,
      dataMode,
      totalUsers: processedAIData.totalUsers,
      overallAITime: processedAIData.overallAITime,
      overallPureAITime: processedAIData.overallPureAITime,
      topApps: processedAIData.mostUsedAIApps.slice(0, 3).map(app => app.applicationName),
      weeksUsed: successfulCrossoverWeeks.length,
      partialData: failedCrossoverWeeks > 0
    });

    return processedAIData;

  } catch (error) {
    console.error('🚨 [4-WEEKLY-AI] Error in fetch4WeeklyAIData:', error);
    throw new Error(`Failed to fetch 4-weekly AI data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Clear 4-weekly AI data cache (for logout or period changes)
 */
export const clear4WeeklyAICache = (): void => {
  if (typeof window !== 'undefined' && (window as any).__4weeklyAICache) {
    (window as any).__4weeklyAICache = {};
    console.log('🗑️ [4-WEEKLY-AI] Cleared AI cache');
  }
};

/**
 * Fetch Non-AI application data from crossover team activities
 * @param weekDate - Week date in YYYY-MM-DD format
 * @param dataMode - Data mode for determining data source
 * @param userToken - User token for My Team mode (optional)
 * @param managerId - Manager ID for My Team mode (optional)
 * @returns Promise<NonAIDataResult> - Non-AI application usage data
 */
export const fetchNonAIData = async (
  weekDate: string,
  dataMode: DataMode,
  userToken?: string,
  managerId?: number
): Promise<NonAIDataResult> => {
  console.log('📱 [NON-AI] Fetching non-AI data:', { weekDate, dataMode });

  try {
    // Basic mode doesn't have team data, return empty result
    if (dataMode === 'basic') {
      console.log('ℹ️ [NON-AI] Basic mode selected, returning empty data');
      return {
        teamMemberApplications: [],
        topApplications: []
      };
    }

    // Fetch team data based on data mode
    let teamData;
    
    if (dataMode === 'central-ps') {
      console.log('🌍 [NON-AI] Fetching comprehensive data for Central PS mode');
      teamData = await fetchComprehensiveData(weekDate);
    } else if (dataMode === 'my-team') {
      if (!userToken || !managerId) {
        throw new Error('User token and manager ID required for My Team mode');
      }
      console.log('👥 [NON-AI] Fetching manager team data for My Team mode');
      teamData = await fetchManagerTeamData(managerId, weekDate, userToken);
    } else {
      throw new Error(`Unsupported data mode: ${dataMode}`);
    }

    // Extract team member activities
    const teamMemberActivities = teamData.activities.map(activity => ({
      grouping: activity.grouping,
      assignment: activity.assignment
    }));

    console.log('🔄 [NON-AI] Processing team member activities:', {
      totalMembers: teamMemberActivities.length
    });

    // Process activities through Non-AI service
    const nonAIResult = NonAIDataService.processNonAIActivities(teamMemberActivities);

    console.log('✅ [NON-AI] Non-AI data processing complete:', {
      weekDate,
      dataMode,
      teamMembers: nonAIResult.teamMemberApplications.length,
      topApplications: nonAIResult.topApplications.length,
      topApp: nonAIResult.topApplications[0]?.applicationName || 'None'
    });

    return nonAIResult;

  } catch (error) {
    console.error('🚨 [NON-AI] Error fetching non-AI data:', error);
    throw new Error(`Failed to fetch non-AI data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Fetch and aggregate Non-AI data for 4-weekly reports
 * This function fetches 4 weeks of team activities and aggregates them for Non-AI Overview
 * @param startDate - Starting Monday date for the 4-week period (YYYY-MM-DD format)
 * @param dataMode - Data mode ('central-ps', 'my-team')
 * @param userToken - User token for My Team mode (optional)
 * @param managerId - Manager ID for My Team mode (optional)
 * @returns Promise<NonAIDataResult> - 4-weekly aggregated Non-AI data
 */
export const fetch4WeeklyNonAIData = async (
  startDate: string,
  dataMode: DataMode,
  userToken?: string,
  managerId?: number
): Promise<NonAIDataResult> => {
  console.log('📱 [4-WEEKLY-NON-AI] Starting 4-weekly Non-AI data fetch:', {
    startDate,
    dataMode,
    managerId
  });

  try {
    // Basic mode doesn't have team data, return empty result
    if (dataMode === 'basic') {
      console.log('ℹ️ [4-WEEKLY-NON-AI] Basic mode selected, returning empty data');
      return {
        teamMemberApplications: [],
        topApplications: []
      };
    }

    // Import required services
    const { fetchComprehensiveData, fetchManagerTeamData } = await import('./multiTeamDataService');
    const { formatTimestampToDate, getMondayMidnightUTC } = await import('./dailyUsageApiService');
    const { NonAIDataService } = await import('./nonAIDataService');

    // Calculate 4 consecutive week dates
    const weekDates = getConsecutiveWeekDates(startDate, 4);
    console.log('📅 [4-WEEKLY-NON-AI] Processing Non-AI data for 4 weeks:', weekDates);

    // Fetch team data for all 4 weeks
    console.log('🏢 [4-WEEKLY-NON-AI] Fetching team data for 4 weeks in parallel...');
    const teamDataPromises = weekDates.map(async (weekDate, index) => {
      try {
        console.log(`🏢 [4-WEEKLY-NON-AI] Fetching Non-AI week ${index + 1}: ${weekDate}`);
        
        // Convert to Monday date format for API
        const mondayTimestamp = getMondayMidnightUTC(weekDate);
        const mondayDate = formatTimestampToDate(mondayTimestamp);
        
        let data;
        if (dataMode === 'central-ps') {
          // Use comprehensive data for Central PS
          data = await fetchComprehensiveData(mondayDate);
        } else if (dataMode === 'my-team') {
          if (!userToken || !managerId) {
            throw new Error('User token and manager ID required for My Team mode');
          }
          // Use manager team data for My Team
          data = await fetchManagerTeamData(managerId, mondayDate, userToken);
        } else {
          throw new Error(`Unsupported data mode: ${dataMode}`);
        }
        
        console.log(`✅ [4-WEEKLY-NON-AI] Non-AI week ${index + 1} complete: ${data.activities.length} activities`);
        return { weekDate, data, success: true };
      } catch (error) {
        console.error(`🚨 [4-WEEKLY-NON-AI] Non-AI week ${index + 1} failed (${weekDate}):`, error);
        return { weekDate, data: { teams: [], activities: [] }, success: false, error };
      }
    });

    // Execute all team data API calls
    const teamDataResults = await Promise.all(teamDataPromises);

    // Process results and check for failures
    const successfulWeeks = teamDataResults.filter(r => r.success).map(r => r.data);
    const failedWeeks = teamDataResults.filter(r => !r.success).length;

    console.log('📱 [4-WEEKLY-NON-AI] API call results:', {
      successfulWeeks: successfulWeeks.length,
      failedWeeks
    });

    // Check if we have sufficient data (at least 3/4 weeks)
    if (successfulWeeks.length < 3) {
      throw new Error(`Insufficient team data for 4-weekly Non-AI report (only ${successfulWeeks.length}/4 weeks available)`);
    }

    // Aggregate team activities across 4 weeks
    const allTeamMemberActivities: any[] = [];
    const userActivityMap = new Map<string, any>();

    // Process each week's activities
    successfulWeeks.forEach((weekData, weekIndex) => {
      weekData.activities.forEach((activity: any) => {
        const userId = activity.assignment?.candidate?.id?.toString() || '';
        const email = activity.assignment?.candidate?.email || '';
        const userKey = userId || email;

        if (!userKey) return; // Skip activities without user identification

        if (userActivityMap.has(userKey)) {
          // Merge activities for the same user across weeks
          const existingActivity = userActivityMap.get(userKey);
          
          // Combine grouping data
          if (activity.grouping?.advancedGroups && existingActivity.grouping?.advancedGroups) {
            // Merge groups by section name
            const groupMap = new Map();
            
            // Add existing groups
            existingActivity.grouping.advancedGroups.forEach((group: any) => {
              if (group.sectionName) {
                groupMap.set(group.sectionName, { ...group });
              }
            });
            
            // Merge new groups
            activity.grouping.advancedGroups.forEach((group: any) => {
              if (group.sectionName) {
                if (groupMap.has(group.sectionName)) {
                  const existingGroup = groupMap.get(group.sectionName);
                  existingGroup.spentTime = (existingGroup.spentTime || 0) + (group.spentTime || 0);
                  
                  // Merge group items
                  const itemMap = new Map();
                  existingGroup.groupItems.forEach((item: any) => {
                    itemMap.set(item.applicationName, { ...item });
                  });
                  
                  group.groupItems.forEach((item: any) => {
                    if (itemMap.has(item.applicationName)) {
                      const existingItem = itemMap.get(item.applicationName);
                      existingItem.spentTime = (existingItem.spentTime || 0) + (item.spentTime || 0);
                    } else {
                      itemMap.set(item.applicationName, { ...item });
                    }
                  });
                  
                  existingGroup.groupItems = Array.from(itemMap.values());
                } else {
                  groupMap.set(group.sectionName, { ...group });
                }
              }
            });
            
            existingActivity.grouping.advancedGroups = Array.from(groupMap.values());
          }
        } else {
          // First time seeing this user
          userActivityMap.set(userKey, {
            grouping: activity.grouping ? JSON.parse(JSON.stringify(activity.grouping)) : null,
            assignment: activity.assignment
          });
        }
      });
    });

    // Convert map back to array
    const aggregatedActivities = Array.from(userActivityMap.values());

    console.log('🔄 [4-WEEKLY-NON-AI] Aggregated team member activities:', {
      totalMembers: aggregatedActivities.length,
      weeksProcessed: successfulWeeks.length
    });

    // Process aggregated activities through Non-AI service
    const nonAIResult = NonAIDataService.processNonAIActivities(aggregatedActivities);

    console.log('✅ [4-WEEKLY-NON-AI] 4-weekly Non-AI data processing complete:', {
      teamMembers: nonAIResult.teamMemberApplications.length,
      topApplications: nonAIResult.topApplications.length,
      weeksAggregated: successfulWeeks.length
    });

    return nonAIResult;

  } catch (error) {
    console.error('🚨 [4-WEEKLY-NON-AI] Error fetching 4-weekly Non-AI data:', error);
    throw new Error(`Failed to fetch 4-weekly Non-AI data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}; 